import React from 'react';
import {
  CheckCircle,
  PhoneDisconnect,
  PhoneSlash,
  PhoneTransfer,
  Voicemail,
  WarningCircle,
} from '@phosphor-icons/react';
import COLORS from '../../../util/constants/colors';

const CALL_OUTCOMES = {
  NONE: 'none',
  GENERIC_VOICEMAIL_LEFT: 'generic_voicemail_left',
  RESULT_DELIVERED_TO_PATIENT: 'result_delivered_to_patient',
  RESULT_DELIVERED_TO_VOICEMAIL: 'result_delivered_to_voicemail',
  TRANSFERRED_TO_PROVIDER: 'transferred_to_provider',
  RESULTS_DELIVERED_AND_PROVIDER_TRANSFER: 'results_delivered_and_provider_transfer',
  USER_HANGUP: 'user_hangup',
  AGENT_HANGUP: 'agent_hangup',
  ERROR: 'error',
  BUSY_SIGNAL: 'busy_signal',
  UNABLE_TO_LEAVE_VOICEMAIL: 'unable_to_leave_voicemail',
  NO_ANSWER: 'no_answer',
  PATIENT_RESULT_DELIVERY_UNVERIFIED: 'patient_result_delivery_unverified',
  RESULT_DELIVERED_VIA_INBOUND: 'result_delivered_via_inbound',
  INBOUND_RESULT_DELIVERY_UNVERIFIED: 'inbound_result_delivery_unverified',
};

const tag = (label, icon, color, backgroundColor) => ({
  label,
  icon,
  color,
  backgroundColor,
});

const successTag = (label, icon = <CheckCircle size={16} />) => (
  tag(label, icon, COLORS.SUCCESS_DARK, COLORS.SUCCESS_TERTIARY_LITE)
);
const attentionTag = (label, icon = <WarningCircle size={16} weight="bold" />) => (
  tag(label, icon, COLORS.ATTENTION_LITE, COLORS.ATTENTION_TERTIARY_LITE)
);
const errorTag = (label) => tag(
  label,
  <WarningCircle size={16} weight="bold" />,
  COLORS.ERROR_LITE,
  COLORS.ERROR_TERTIARY_LITE,
);
const hangupTag = (label) => tag(
  label,
  <PhoneDisconnect size={16} weight="bold" />,
  'black',
  COLORS.GREY,
);

const tagsByOutcome = {
  [CALL_OUTCOMES.NONE]: errorTag('No Outcome'),
  [CALL_OUTCOMES.GENERIC_VOICEMAIL_LEFT]: successTag('Generic Voicemail Left'),
  [CALL_OUTCOMES.RESULT_DELIVERED_TO_PATIENT]: successTag('Results Delivered to Patient'),
  [CALL_OUTCOMES.RESULT_DELIVERED_TO_VOICEMAIL]: successTag('Results Delivered to Voicemail'),
  [CALL_OUTCOMES.TRANSFERRED_TO_PROVIDER]: tag(
    'Transferred to Provider',
    <PhoneTransfer size={16} weight="bold" />,
    COLORS.BLUE_PRIMARY_LITE,
    COLORS.LAVENDER,
  ),
  [CALL_OUTCOMES.RESULTS_DELIVERED_AND_PROVIDER_TRANSFER]: successTag(
    'Results Delivered and Provider Transfer',
    <CheckCircle size={18} weight="bold" />,
  ),
  [CALL_OUTCOMES.AGENT_HANGUP]: hangupTag('Agent Hangup'),
  [CALL_OUTCOMES.USER_HANGUP]: hangupTag('User Hangup'),
  [CALL_OUTCOMES.ERROR]: errorTag('Error'),
  [CALL_OUTCOMES.BUSY_SIGNAL]: attentionTag(
    'Busy Signal',
    <PhoneSlash size={16} weight="bold" />,
  ),
  [CALL_OUTCOMES.UNABLE_TO_LEAVE_VOICEMAIL]: attentionTag(
    'Unable to Leave Voicemail',
    <Voicemail size={16} weight="bold" />,
  ),
  [CALL_OUTCOMES.NO_ANSWER]: attentionTag(
    'No Answer',
    <PhoneSlash size={16} weight="bold" />,
  ),
  [CALL_OUTCOMES.PATIENT_RESULT_DELIVERY_UNVERIFIED]: attentionTag('Results Delivery Unverified'),
  [CALL_OUTCOMES.RESULT_DELIVERED_VIA_INBOUND]: successTag('Results Delivered via Inbound'),
  [CALL_OUTCOMES.INBOUND_RESULT_DELIVERY_UNVERIFIED]: attentionTag('Inbound Delivery Pending Verification'),
};

const CALL_OUTCOME_TAGS = Object.fromEntries(
  Object.entries(tagsByOutcome).map(([value, tagDef]) => [value, { ...tagDef, value }]),
);

// Whether the Call Outcome filter should offer `outcome` as a selectable
// option. The server owns the gating policy and redacts gated outcomes from the
// data itself, so this is only used to hide dropdown options the backend can't
// (an option corresponds to no row). `gatedOutcomes` comes from the index
// response (`inbound_delivery_outcomes`) — the single source of truth — rather
// than a hardcoded list here.
const isOutcomeVisible = (outcome, inboundDeliveryOutcomesEnabled = false, gatedOutcomes = []) => (
  inboundDeliveryOutcomesEnabled || !gatedOutcomes.includes(outcome)
);

const CALL_OUTCOME_TAG_STYLE = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
  borderRadius: 12,
  padding: '2px 8px',
  // width: 'fit-content',
  textAlign: 'center',
  fontSize: 12,
  fontWeight: 600,
  maxWidth: 150,
};

const stringifyOptions = {
  format: 'RFC1738',
  addQueryPrefix: true,
};

export {
  CALL_OUTCOME_TAGS,
  CALL_OUTCOME_TAG_STYLE,
  CALL_OUTCOMES,
  isOutcomeVisible,
  stringifyOptions,
};
